import { Timestamp } from 'firebase/firestore'

export interface Company {
	active: boolean
	clientCode: string
	logoLink?: string
	color: string
	id: string
	url: string
	createdOn: Timestamp
	infoPDF: string
	legalName: string
	name: string
	daysAfterToApprove: number
	programStartDate: string
	supportEmail: string
	disabledPagesByRole: Record<Role, string[]>
	bonusLevelsEnabled: boolean;
	bonusLevelsResetQuarterly: boolean;
	shouldShowYTD: boolean
	requiredFields?: Partial<Record<Role, (keyof User)[]>>;
	roleLanding?: Partial<Record<Role, string>>;
}

type ConsentSource = 'signup' | 'user-settings' | 'admin' | 'import';
type ConsentMeta = { at: Timestamp; source: ConsentSource };

export interface User {
	createdOn: Timestamp
	email: string
	firstName: string
	lastName: string
	lastLogin: Timestamp
	fullName: string
	role: Role
	managerUID: string
	officeAddress: string
	officeCity: string
	officeName: string
	officeNumber: string
	officeState: string
	officeZip: string
	phoneNumber?: string
	pushToken: string
	region: string
	uid: string
	mustTextStartToEnableSmsAt?: Timestamp;
	notificationPreference: 'email' | 'sms'
	adminNotificationOptIn: { email: boolean; sms: boolean }
	transactionalOptIn: { email: boolean; sms: boolean }
	consentMeta?: {
		email?: {
			adminNotification?: ConsentMeta
			transactional?: ConsentMeta
		}
		sms?: {
			adminNotification?: ConsentMeta
			transactional?: ConsentMeta
		}
	}
	verified: boolean
}

export interface CustomFile {
	file?: File
	image?: string
}

export interface CompanyCode {
	code: string
	companyID: string
	companyName: string
	createdByID: string
	createdOn: Timestamp
	isTemp: boolean
	role: Role
	lockEmail: boolean
	userEmail?: string
}

export enum Role {
	REALTOR = 'REALTOR',
	SALES = 'SALES',
	MANAGER = 'MANAGER',
	ADMIN = 'ADMIN',
	ACCOUNT_OWNER = 'ACCOUNTOWNER'
}

interface InternalUserListData {
	email: string;
	firstName?: string;
	lastName?: string;
	phoneNumber?: string;
	role: string;
	lockEmail?: boolean;
}

export interface UserListData {
	id: string
	userUID: string
	userList: InternalUserListData[]
	createdOn: Timestamp
	status: 'rejected' | 'accepted' | 'pending'
	errorMessage: string
}

export interface Region {
	name: string
	id: string
	createdOn: Timestamp
	createdByID: string
	searchTerm: string[]
}

export interface Comunity {
	name: string
	id: string
	region: string
	searchTerm: string[]
	createdOn: Timestamp
	updatedOn: Timestamp
	updatedByID: string
}

export interface Reward {
	id: string
	name: string,
	points: number,
	mustHaveClient: boolean
	active: boolean
	searchTerms: string[]
	createdOn: Timestamp
	updatedByID: string
	updatedOn: Timestamp
}

export interface PendingPoints {
	id?: string;
	totalPoints: number;
	status: 'pending' | 'approved' | 'denied';
	approvedByName: string;
	approvedByUID: string;
	createdOn: Timestamp
	programYear: number
	searchTerms: string[]
	isClaimed?: boolean          // Optional for if there is an assigned realtor or not

	// Sales & Manager info
	salesRepUID: string;
	salesRepName: string;
	managerUID: string;

	// Realtor info
	realtorUID?: string;         // Optional for "on hold" records
	realtorName?: string;        // Optional for "on hold" records
	realtorEmail: string;        // Always required to track who it’s for

	// Submission metadata
	clientName?: string;
	community: string;
	date: string;
	region: string;

	// Array of point entries (can be rewards or something else)
	points: Reward[];
}

export interface BonusLevel {
	id: string;
	name: string;
	minPoints: number | string;
	rank: number;
	reward: string;
}

export interface BonusLevels {
	levels: BonusLevel[];
	levelsUpdatedOn: Timestamp;
	levelsUpdatedByID: string;
}

export interface NotificationInput {
	id: string
	title: string
	body: string

	deliveryMethods: ('emailOrSms' | 'inApp')[] // both can be selected

	kind: 'admin' | 'transactional'

	channels?: {
		email?: {
			templateId?: string
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			variables?: Record<string, any>
			subjectOverride?: string
			fromOverride?: string
			replyTo?: string
		}
		// sms intentionally omitted; we use `body` directly
	}

	sendAt: Date // optional scheduling
	persistent?: boolean // for in-app
	expiresAt?: Date  // only if persistent

	action?: {
		label: string
		url: string
	}

	regionIDs: string[]
	recipientUIDs: string[] // can be 1 or many
	count?: number
	createdOn: Timestamp
	createdByID: string
}

export interface Notification {
	id: string
	recipientUID: string

	title: string
	body: string

	deliveryMethods: ('emailOrSms' | 'inApp')[]

	kind: 'admin' | 'transactional'

	channels?: {
		email?: {
			templateId?: string
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			variables?: Record<string, any>
			subjectOverride?: string
			fromOverride?: string
			replyTo?: string
		}
		// sms intentionally omitted; we use `body` directly
	}

	// In-App Notification Options
	persistent?: boolean
	expiresAt?: Timestamp
	dismissedAt?: Timestamp
	readAt?: Timestamp
	deletedAt?: Timestamp

	// Optional call-to-action
	action?: {
		label: string
		url: string // can be internal route like '/reports/123'
	}

	sid?: string
	deliveryStatus?: string
	errorCode?: string
	errorMessage?: string
	lastStatusAt?: Timestamp
	createdOn: Timestamp
	createdByID: string
	processedAt?: Timestamp
}

type PeriodScope = 'ytd' | 'quarter';

export interface AutoNotification {
	id: string;
	enabled: boolean;
	name: string;
	title: string;
	targetPoints: number;
	periodScope: PeriodScope;          // 'ytd' | 'quarter'
	message: string;
	sendTo: {
		realtor: boolean;                // send to earner
		staffUIDs: string[];             // also notify selected users
	};
	createdAt: Timestamp;
	updatedAt: Timestamp;
	searchTerms: string[]
}


export interface Event {
  id: string;

  // Content
  title: string;
  description?: string; // Markdown/HTML allowed

  // Timing
  startsAt: Timestamp;
  endsAt?: Timestamp;
  allDay?: boolean;
  timezone?: string; // e.g., "America/Chicago"

  // Location (flat for V1)
  kind?: 'text' | 'map' | 'virtual';
  locationLabel?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  url?: string; // map/meeting link

  // Notifications (per-event selection)
  deliveryMethods: ('emailOrSms' | 'inApp')[]

  // Audit
  createdOn: Timestamp;
  createdByID: string;
  updatedOn?: Timestamp;
  updatedByID?: string;
}

export interface ClientRegistrationBase {
	id: string
	createdOn: Timestamp
	createdByID: string
	createdByName: string
	submittedOn?: Timestamp
}

export interface ClientRegistrationHtml {
  id: string
  html: string
  createdOn: Timestamp
  createdByID: string
}

export type ClientRegistrationColumnDef<T> = {
  id: string;
  label: string;
  align?: 'left' | 'right' | undefined;
  sortKey: keyof T;
  render: (row: T) => React.ReactNode;
};


export interface RealtorStats {
  // Current program year totals
  approvedYtdSum: number;
  approvedYtdCount: number;

  // Per-quarter breakdowns
  approvedQ1Sum?: number;
  approvedQ1Count?: number;

  approvedQ2Sum?: number;
  approvedQ2Count?: number;

  approvedQ3Sum?: number;
  approvedQ3Count?: number;

  approvedQ4Sum?: number;
  approvedQ4Count?: number;

  // Pending and Denied (active records and lifetime within this program year)
  pendingSum: number;
  pendingCount: number;

  deniedSum: number;
  deniedCount: number;

  bonusLevel?: BonusLevel

  // Metadata
  updatedAt?: Timestamp;
}

export interface PointsReport {
	id: string
	companyID: string
	requestedBy: { uid: string; email: string; name?: string }
	params: {
		startDate: Timestamp
		endDate: Timestamp
		regionID: string
		includedPointsID: string[]      // your selected point type IDs
		showBonusLevels?: boolean
	}

	// minimal ops fields
	status: 'queued' | 'running' | 'done' | 'error'
	rowCount?: number
	csvPath?: string            // gs://bucket/path.csv
	signedUrl?: string          // convenience link
	errorMessage?: string
	attempts?: number

	createdAt: Timestamp
	updatedAt: Timestamp
	expiresAt?: Timestamp // for TTL
}
